import { GuestAvailabilityCalendar } from "@/components/GuestAvailabilityCalendar";
import { PhoneField } from "@/components/PhoneField";
import { LODGE_EMAIL, LODGE_PHONE_DISPLAY } from "@/lib/contact-info";
import { createFileRoute } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { notifyBookingRequest } from "@/lib/notifications.functions";
import { seoHead } from "@/lib/seo";
import { useState } from "react";

export const Route = createFileRoute("/booking")({
  head: () =>
    seoHead({
      path: "/booking",
      title: "Book Your Stay — Coffman Cove Lodging",
      description:
        "Reserve your lodge stay and self-guided boat rental dates at Coffman Cove Lodging on Prince of Wales Island, Alaska.",
    }),
  component: Booking,
});

// Coffman Cove Lodging is strictly a self-guided operation: we rent lodging,
// boats and gear. We do not provide guides, captains, or charter service, and
// no package on this page may be described in those terms.
const PACKAGES = [
  {
    id: "lodge",
    name: "Lodge Stay",
    price: 200,
    unit: "per night",
    includes: [
      "Private room",
      "Meals provided",
      "Fish processing available",
      "Skiff & boat rentals available",
    ],
  },
  {
    id: "self-guided",
    name: "Self-Guided Fishing",
    prices: [
      { price: 400, unit: "per day — boat rental" },
      { text: "Skiffs — inquire for availability" },
    ],
    includes: [
      "Self-run rental boat/skiff",
      "Bait, tackle, rods included",
      "Fish processing optional add-on",
    ],
    footnote:
      "* Rental prices exclude any damages to equipment or vessels. Fuel not included.",
    featured: true,
  },
  {
    id: "hedges-processing",
    name: "Hedges Marine Processing",
    price: 3,
    unit: "per lb (~$150 per box)",
    includes: [
      "Fish cleaning & filleting",
      "Vacuum-seal & flash freeze",
      "Boxing for travel",
    ],
    link: "https://hedgesmarine.my.canva.site/?fbclid=IwY2xjawTjHrhwZG9mBWV4dG4DYWVtAjEwAGJyaWQRMXpLZGdYeXhQZXYwUk5MTUNzcnRjBmFwcF9pZBAyMjIwMzkxNzg4MjAwODkyAAEeocyk7OdV_qM5w6Pomv9k5aIb-FZaMIBMJbS4hJjDDlBxO6UlIJPFAFqBtPY_aem_LFe0fEy9XYrKdVjxGKhjuw",
    linkLabel: "Check Website",
  },
];


function Booking() {
  const [pkg, setPkg] = useState("self-guided");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const fd = new FormData(e.currentTarget);
    const str = (k: string) => (fd.get(k) as string | null)?.trim() || null;
    setSubmitting(true);
    try {
      const payload = {
        source: "booking" as const,
        name: str("name") ?? "",
        email: str("email") ?? "",
        phone: str("phone"),
        arrival_date: str("arrival"),
        departure_date: str("departure"),
        party_size: str("anglers") ? Number(str("anglers")) : null,
        package: PACKAGES.find((p) => p.id === pkg)?.name ?? pkg,
        target_species: str("target"),
        notes: [
          `Boats: ${str("boats") ?? "0"}`,
          `Fish processing: ${fd.get("processing") ? "Yes" : "No"}`,
          str("notes"),
        ]
          .filter(Boolean)
          .join("\n"),
      };
      const { error: insertErr } = await supabase.from("booking_requests").insert(payload);
      if (insertErr) throw insertErr;
      setSent(true);
      void notifyBookingRequest({
        data: {
          source: "booking",
          name: payload.name,
          email: payload.email,
          phone: payload.phone,
          arrival: payload.arrival_date,
          departure: payload.departure_date,
          partySize: payload.party_size,
          packageName: payload.package,
          target: payload.target_species,
          notes: payload.notes,
        },
      }).catch(() => {});
    } catch {
      setError(
        `We couldn't send your booking request. Please call ${LODGE_PHONE_DISPLAY} or email ${LODGE_EMAIL}.`,
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <section className="bg-[var(--color-deep)] text-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-16 sm:py-20">
          <p className="uppercase tracking-[0.3em] text-xs text-white/80">Booking</p>
          <h1 className="font-display text-4xl sm:text-5xl mt-3 text-balance max-w-3xl text-white">
            Reserve your week on Prince of Wales Island.
          </h1>
          <p className="mt-4 text-white/85 max-w-2xl">
            All trips are strictly self-guided — you run your own boat. Send your preferred dates,
            and our team will confirm availability within 24 hours. Peak season (June–August) books
            out fast — secure your spot early.
          </p>
        </div>
      </section>
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-16 sm:py-20">


      <div className="mt-10 grid lg:grid-cols-3 gap-5">
        {PACKAGES.map((p) => {
          const link = "link" in p ? (p.link as string) : null;
          const linkLabel = "linkLabel" in p ? (p.linkLabel as string) : null;
          const footnote = "footnote" in p ? (p.footnote as string) : null;
          const priceLines: { price?: number; unit?: string; text?: string }[] =
            "prices" in p
              ? (p.prices as { price?: number; unit?: string; text?: string }[])
              : [{ price: (p as { price: number }).price, unit: (p as { unit: string }).unit }];
          return (
          <div
            key={p.id}
            className={`flex flex-col rounded-xl border-2 p-6 transition-all ${
              pkg === p.id
                ? "border-[var(--color-ember)] bg-card shadow-[0_8px_30px_-10px_var(--color-ember)]"
                : "border-border bg-card/50 hover:border-[var(--color-ember)]/50"
            }`}
          >
          <button onClick={() => setPkg(p.id)} className="flex-1 text-left">

            {p.featured && (
              <span className="inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-[var(--color-ember)] text-[var(--color-ember-foreground)] mb-3">
                Most Popular
              </span>
            )}
            <h3 className="font-display text-2xl">{p.name}</h3>
            {priceLines.map((pl) =>
              "text" in pl && pl.text ? (
                <p className="mt-2 text-sm font-medium text-muted-foreground" key={pl.text}>
                  {pl.text}
                </p>
              ) : (
                <p className="mt-2" key={pl.unit}>
                  <span className="text-3xl font-semibold text-[var(--color-ember)]">${pl.price}</span>
                  <span className="text-sm text-muted-foreground ml-1">{pl.unit}</span>
                </p>
              ),
            )}
            <ul className="mt-4 space-y-1.5 text-sm text-muted-foreground">
              {p.includes.map((i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-[var(--color-ember)]">✓</span>
                  {i}
                </li>
              ))}
            </ul>
            {footnote && (
              <p className="mt-4 text-xs text-muted-foreground italic">{footnote}</p>
            )}
          </button>
          {link && (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 self-start text-sm font-medium text-[var(--color-ember)] underline underline-offset-4 hover:opacity-80"
            >
              {linkLabel ?? link}
            </a>
          )}
          </div>
          );
        })}
      </div>


      <div className="mt-12">
        <GuestAvailabilityCalendar />
      </div>

      <div className="mt-12 rounded-xl border border-border bg-card p-8">
        <h2 className="font-display text-2xl">Request your dates</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Selected: <span className="text-foreground font-medium">{PACKAGES.find((p) => p.id === pkg)?.name}</span>
        </p>

        {sent ? (
          <div className="mt-8 rounded-lg border border-[var(--color-ember)]/40 bg-background p-8 text-center">
            <p className="font-display text-2xl">Your booking request is in.</p>
            <p className="mt-2 text-muted-foreground">
              We'll confirm availability and send a deposit invoice within 24 hours.
            </p>
          </div>
        ) : (
          <form
            className="mt-6 grid gap-5"
            onSubmit={handleSubmit}
          >
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Full name" name="name" required />
              <Field label="Email" name="email" type="email" required />
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              <PhoneField label="Phone" name="phone" />
              <Field label="Arrival" name="arrival" type="date" required />
              <Field label="Departure" name="departure" type="date" required />
            </div>
            <p className="-mt-2 text-sm text-muted-foreground">
              Weeks run Friday to Friday. Arriving guests come in Friday midday and
              evening; the week before departs Friday morning.
            </p>
            <div className="grid md:grid-cols-2 gap-5">
              <label className="grid gap-1.5">
                <span className="text-sm font-medium">Number of anglers</span>
                <select name="anglers" defaultValue="2" className="rounded-md border border-input bg-background px-3 py-2">
                  {Array.from({ length: 26 }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              <label className="grid gap-1.5">
                <span className="text-sm font-medium">Primary target</span>
                <select name="target" className="rounded-md border border-input bg-background px-3 py-2">
                  <option>King Salmon</option>
                  <option>Halibut</option>
                  <option>Coho / Silvers</option>
                  <option>Mixed bag</option>
                </select>
              </label>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <label className="grid gap-1.5">
                <span className="text-sm font-medium">Boats</span>
                <select name="boats" defaultValue="0" className="rounded-md border border-input bg-background px-3 py-2">
                  {[0, 1, 2, 3, 4, 5, 6].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-3 md:self-end md:pb-2">
                <input
                  type="checkbox"
                  name="processing"
                  className="h-4 w-4 rounded border-input accent-[var(--color-ember)]"
                />
                <span className="text-sm font-medium">Fish processing</span>
              </label>
            </div>
            <label className="grid gap-1.5">
              <span className="text-sm font-medium">Notes for our team</span>
              <textarea
                name="notes"
                rows={4}
                className="rounded-md border border-input bg-background px-3 py-2"
                placeholder="Experience level, dietary needs, special requests…"
              />
            </label>
            {error && (
              <p role="alert" className="text-sm font-medium text-[var(--color-ember)]">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="justify-self-start disabled:opacity-60 rounded-md bg-[var(--color-ember)] px-8 py-3 text-sm font-semibold uppercase tracking-wider text-[var(--color-ember-foreground)] hover:opacity-90"
            >
              {submitting ? "Sending…" : "Request booking"}
            </button>
          </form>
        )}
      </div>
      </div>
    </div>
  );
}


function Field(props: React.InputHTMLAttributes<HTMLInputElement> & { label: string }) {
  const { label, ...rest } = props;
  return (
    <label className="grid gap-1.5">
      <span className="text-sm font-medium">{label}</span>
      <input {...rest} className="rounded-md border border-input bg-background px-3 py-2" />
    </label>
  );
}
